import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import PaginatedResponse, { Paginated } from "_shared/_api/pagination/responses/paginated-response";
import { api } from "_shared/_stores/api";
import BulkPermissionCommand from "permissions/_api/request-contracts/bulk-permission-command";
import PermissionCommand from "permissions/_api/request-contracts/permission-command";
import PermissionQuery, { PermissionView } from "permissions/_api/queries/permission-query";
import PermissionSchema, { PermissionJSON } from "permissions/_schemas/permission-schema";
import PermissionsByGranteeSchema, {
  PermissionsByGranteeJSON,
} from "permissions/by-grantee/_schemas/permissions-by-grantee-schema";
import Permission from "permissions/permission";
import { z } from "zod";

type PermissionQueryArgs = {
  query: PermissionQuery;
};

type SessionPermissionQueryArgs = {
  query?: PermissionQuery;
};

type PermissionMutationArgs = {
  permission: Permission;
};

type BulkPermissionMutationArgs = {
  permissions: Permission[];
  replaceMatchingResourceTypes?: boolean;
};

export type RegrantedPermissionJSON = PermissionJSON & {
  supersededPermissionId?: string | null;
};

const RegrantedPermissionJSONZ = PermissionSchema.PermissionJSONZ.extend({
  supersededPermissionId: z.uuid().nullish(),
});

const jsonPatchHeaders = {
  "content-type": "application/json-patch+json",
};

const permissionListTag = { type: "Permission" as const, id: "LIST" };
export const sessionPermissionTag = { type: "Permission" as const, id: "SESSION" };
const getPermissionMutationTags = () => [permissionListTag, sessionPermissionTag];

export const permissionsApi = api.injectEndpoints({
  endpoints: (build) => ({
    addPermission: build.mutation<PermissionJSON, PermissionMutationArgs>({
      query: ({ permission }) => ({
        url: "permissions",
        method: "POST",
        body: JSON.stringify(new PermissionCommand(permission)),
        headers: jsonPatchHeaders,
      }),
      transformResponse: (response: unknown) => PermissionSchema.validateJSON(response),
      invalidatesTags: getPermissionMutationTags,
    }),
    updatePermission: build.mutation<PermissionJSON, PermissionMutationArgs>({
      query: ({ permission }) => {
        if (!permission.id) {
          throw new Error("Permission ID is required to update a permission.");
        }

        return {
          url: `permissions/${permission.id.value}`,
          method: "PUT",
          body: JSON.stringify(new PermissionCommand(permission)),
          headers: jsonPatchHeaders,
        };
      },
      transformResponse: (response: unknown) => PermissionSchema.validateJSON(response),
      invalidatesTags: getPermissionMutationTags,
    }),
    bulkAddPermissions: build.mutation<PermissionJSON[], BulkPermissionMutationArgs>({
      query: ({ permissions, replaceMatchingResourceTypes }) => ({
        url: "permissions/bulk",
        method: "POST",
        body: JSON.stringify(new BulkPermissionCommand(permissions, replaceMatchingResourceTypes)),
        headers: jsonPatchHeaders,
      }),
      transformResponse: (response: unknown) =>
        requireArray(response, "bulk permissions response").map((permission) =>
          PermissionSchema.validateJSON(permission),
        ),
      invalidatesTags: getPermissionMutationTags,
    }),
    getPermissions: build.query<Paginated<PermissionJSON>, PermissionQueryArgs>({
      serializeQueryArgs: ({ queryArgs }) => queryArgs.query.cacheKey,
      query: ({ query }) => {
        if (query.permissionView === PermissionView.Grantee) {
          throw new Error(
            "The getPermissions method is not supported when the 'Grantee' permissionView is specified in the permission query.",
          );
        }

        return {
          url: "permissions",
          params: query.searchParams,
        };
      },
      transformResponse: (response: unknown, meta): Paginated<PermissionJSON> => {
        const permissionJson = requireArray(response, "permissions response").map((permission) =>
          PermissionSchema.validateJSON(permission),
        );
        return {
          data: permissionJson,
          headers: PaginatedResponse.parsePaginationHeader(meta?.response?.headers.get("x-pagination") ?? ""),
        };
      },
      providesTags: [permissionListTag],
    }),
    getSessionPermissions: build.query<Paginated<PermissionJSON> | undefined, SessionPermissionQueryArgs>({
      serializeQueryArgs: ({ queryArgs }) => queryArgs.query?.cacheKey ?? "empty",
      queryFn: async ({ query }, _api, _extraOptions, baseQuery) => {
        if (!query) {
          return { data: PaginatedResponse.empty<PermissionJSON>() };
        }

        const result = await baseQuery({
          url: "permissions",
          params: query.searchParams,
        });

        if (result.error) {
          return { error: result.error };
        }

        try {
          // Per grant, not all-or-nothing. These are the permissions the whole session is built from:
          // the navigation is drawn from them and every guarded route waits on them, so rejecting the
          // response because one grant in it will not parse costs the user their entire application --
          // a stripped navigation and a page that loads forever. Dropping the unreadable grant denies
          // that one permission and leaves every other one working, which is both the safe direction
          // and a far smaller blast radius than the alternative.
          //
          // It is not silent: validateJSON has already logged the schema failure and the payload that
          // caused it, and the warning below names how many grants were lost.
          const permissionJson: PermissionJSON[] = [];
          let unreadable = 0;
          for (const permission of requireArray(result.data, "session permissions response")) {
            try {
              permissionJson.push(PermissionSchema.validateJSON(permission));
            } catch {
              unreadable += 1;
            }
          }
          if (unreadable > 0) {
            console.warn(
              `${unreadable} session permission(s) could not be read and have been treated as not granted. ` +
                "The schema failure above names the grant.",
            );
          }
          return {
            data: {
              data: permissionJson,
              headers: PaginatedResponse.parsePaginationHeader(result.meta?.response?.headers.get("x-pagination") ?? ""),
            },
          };
        } catch (error) {
          return { error: toCustomError(error) };
        }
      },
      providesTags: [sessionPermissionTag],
    }),
    getPermissionsByGrantee: build.query<Paginated<PermissionsByGranteeJSON>, PermissionQueryArgs>({
      serializeQueryArgs: ({ queryArgs }) => queryArgs.query.cacheKey,
      query: ({ query }) => {
        if (query.permissionView !== PermissionView.Grantee) {
          throw new Error(
            "The getPermissionsByGrantee method is not supported if the 'Grantee' permissionView is NOT specified in the permission query.",
          );
        }

        return {
          url: "permissions",
          params: query.searchParams,
        };
      },
      transformResponse: (response: unknown, meta): Paginated<PermissionsByGranteeJSON> => {
        const permissionsByGranteeJson = requireArray(response, "permissions by grantee response").map((permission) =>
          PermissionsByGranteeSchema.validateJSON(permission),
        );
        return {
          data: permissionsByGranteeJson,
          headers: PaginatedResponse.parsePaginationHeader(meta?.response?.headers.get("x-pagination") ?? ""),
        };
      },
      providesTags: [permissionListTag],
    }),
    disablePermission: build.mutation<PermissionJSON, PermissionMutationArgs>({
      query: ({ permission }) => {
        if (!permission.id) {
          throw new Error("Permission ID is required to disable a permission.");
        }

        return {
          url: `permissions/${permission.id.value}`,
          method: "DELETE",
          params: { version: permission.version },
        };
      },
      transformResponse: (response: unknown) => PermissionSchema.validateJSON(response),
      invalidatesTags: getPermissionMutationTags,
    }),
    /**
     * Restores access the administrator revoked. The server re-grants rather than enables — it
     * mints a fresh permission and leaves the named one disabled, so the record of who revoked
     * access survives — which is why the route is `re-grant` and the response carries a different
     * id than the one sent. The list tags are invalidated, so the refetch picks the new grant up;
     * a caller that held on to the old id would re-grant it again and stack a row per call.
     */
    enablePermission: build.mutation<RegrantedPermissionJSON, PermissionMutationArgs>({
      query: ({ permission }) => {
        if (!permission.id) {
          throw new Error("Permission ID is required to enable a permission.");
        }

        return {
          url: `permissions/${permission.id.value}/re-grant`,
          method: "POST",
          params: { version: permission.version },
          body: {},
        };
      },
      transformResponse: (response: unknown) => RegrantedPermissionJSONZ.parse(response),
      invalidatesTags: getPermissionMutationTags,
    }),
  }),
});

function requireArray(value: unknown, description: string): unknown[] {
  if (Array.isArray(value)) return value;
  throw new Error(`Expected ${description} to be an array.`);
}

function toCustomError(error: unknown): FetchBaseQueryError {
  return {
    status: "CUSTOM_ERROR",
    error: error instanceof Error ? error.message : String(error ?? "An error occurred."),
  };
}

export const {
  useAddPermissionMutation,
  useUpdatePermissionMutation,
  useBulkAddPermissionsMutation,
  useGetPermissionsQuery,
  useLazyGetPermissionsQuery,
  useGetSessionPermissionsQuery,
  useGetPermissionsByGranteeQuery,
  useDisablePermissionMutation,
  useEnablePermissionMutation,
} = permissionsApi;
